import { useEffect, useMemo, useState, useRef, Dispatch, SetStateAction } from 'react';
import moment from 'moment';
import { fetchPCConsoleKPITrendsCompareTrends } from '@intelligence/services/topcharts';
import {
  getTopList,
  getMaketsType,
  getFixedTableData,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utilsTmp/countryUtils';
import {
  getAllMarkets,
  formatFetchParams,
  formatLineChartData,
  formatDeviceBarData,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/UserEngagementAmpere/utils';
import { getDefaultTime } from '@intelligence/utils/topchartsUtils';
import { fillInEmptyData } from '@intelligence/pages/Compare/PcConsoleGamesBeta/components/VisualList/hooks/utils';

const COUNTRIES_CHART_TOP_COUNT = 20;
const COUNTRIES_CHART_DISPLAY_COUNT = COUNTRIES_CHART_TOP_COUNT + 1;
const OTHERS_MARKET = 'others';

interface IUseGetCountriesEngagementDataProps {
  regionOptions: any;
  loading?: boolean;
  fetchParams?: { [key: string]: any };
  setFetchParams?: any;
  shouldFetchDataByFetchParamsChangeRef?: any;
  isTwoMonths?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  marketChartType: string;
  filterLoading?: boolean;
  isBasicLoading?: boolean;
  isOperateDetail?: boolean;
  getExtendParams?: () => void;
  updateEarliestDateData?: Function;
  hasByDeviceBar?: boolean;
}

function useGetCountriesEngagementData(props: IUseGetCountriesEngagementDataProps) {
  const {
    regionOptions,
    fetchParams,
    setFetchParams,
    loading,
    setLoading,
    shouldFetchDataByFetchParamsChangeRef,
    isTwoMonths,
    marketChartType,
    filterLoading,
    isBasicLoading,
    isOperateDetail,
    getExtendParams,
    updateEarliestDateData,
    hasByDeviceBar,
  } = props;

  // 表格数据
  const [marketTableList, setMarketTableList] = useState<any>([]);
  // 分设备柱图
  const [deviceBarData, setDeviceBarData] = useState<any[]>([]);
  const [deviceBarOthersData, setDeviceBarOthersData] = useState<any[]>([]);
  // 折线图原始数据
  const [marketLineData, setMarketLineData] = useState<any>([]);
  const [marketLineOthersData, setMarketLineOthersData] = useState<any[]>([]);
  // 柱图、饼图数据
  const [marketBarPieChartList, setMarketBarPieChartList] = useState<any>([]);

  // 请求折线图时的 loading
  const [marketLineLoading, setMarketLineLoading] = useState(true);
  // 请求图表时的 loading 态
  const [countryLineLoading, setCountryLineLoading] = useState(true);
  // 请求国家数据 loading 态
  const [marketLoading, setMarketLoading] = useState(loading);

  // 请求错误态
  const [marketError, setMarketError] = useState(false);

  // 表格固定列
  const [marketFixedTableData, setMarketFixedTableData] = useState<any[]>([]);

  // 用于计算饼图 other 指标数据
  const [topList, setTopList] = useState<any>({
    total: {},
    global: {},
    globalWoChina: {},
    list: [],
  });

  // 是否已继承参数，继承逻辑只初始化执行一次，如继承后再触发变动不再执行继承逻辑
  const hasExtendedRef = useRef(false);

  // 用于折线图请求参数，避免折线参数使用 useMemo 状态未监听导致获取到上次数据
  const topMarketsRef = useRef<string[]>([]);
  const fetchSequenceRef = useRef(0);
  const inFlightFetchSignatureRef = useRef<string | null>(null);

  const buildFetchSignature = (params: Record<string, any>) => {
    return JSON.stringify({
      metric: params.metric,
      date_type: params.date_type,
      start_date: params.start_date,
      end_date: params.end_date,
      region_type: params.region_type,
      regions: params.regions,
      markets: params.markets,
      device: params.device,
      data_sources: params.data_sources,
      entity_type: params.entity_type,
      hasByDeviceBar,
      marketChartType,
    });
  };

  // 获取前 20 名国家地区，查询折线图数据
  const topMarkets = useMemo(() => {
    const topMarketList = marketBarPieChartList?.map((item: { market: string }) => item?.market) || [];
    topMarketsRef.current = topMarketList;
    return topMarketList;
  }, [marketBarPieChartList]);

  // 分设备柱图，根据表格数据取前 20 项数据，避免合计排序和表格顺序不一致
  const topDeviceBarData = useMemo(() => {
    const showTopDeviceBarData = deviceBarData?.filter(
      (item) => topMarkets?.includes(item.market) || item.market === 'Others',
    );
    return [...(showTopDeviceBarData || []), ...(deviceBarOthersData || [])];
  }, [deviceBarData, topMarkets, deviceBarOthersData]);

  // 折线图数据展示前 20 项数据，全量地区数据筛选出表格前 20 项
  const topLineChartData = useMemo(() => {
    const showTopMarketLineData = marketLineData?.filter((item: any) => topMarkets?.includes(item.market));
    return [...(showTopMarketLineData || []), ...(marketLineOthersData || [])];
  }, [marketLineData, topMarkets, marketLineOthersData]);

  // 获取全部 markets 国家地区
  const { markets: allMarkets, marketsLocalMap } = useMemo(() => getAllMarkets(regionOptions), [regionOptions]);

  // 根绝请求的数据，来指定当前时间筛选器的值
  const updateFetchParamsTime = (startDate: string, endDate: string) => {
    // 自动触发 fetchParams 不触发请求逻辑，将标识置为 false
    shouldFetchDataByFetchParamsChangeRef.current = false;

    if (startDate && endDate) {
      setFetchParams((prev: { [key: string]: any }) => ({
        ...prev,
        start_date: startDate,
        end_date: endDate,
      }));
    } else {
      const [defaultStart, defaultEnd] = getDefaultTime(fetchParams?.date_type, isTwoMonths);

      setFetchParams((prev: { [key: string]: any }) => ({
        ...prev,
        start_date: defaultStart,
        end_date: defaultEnd,
      }));
    }
  };

  // 获取其他国家数据
  const getOthersData = async ({
    params,
    isByDevice = false,
    isByDate = false,
    // 将响应数据的起止时间返回
    returnTime = false,
  }: {
    params: Record<string, any>;
    isByDevice?: boolean;
    isByDate?: boolean;
    returnTime?: boolean;
  }) => {
    const currentParams = { ...params };
    // 其他数据不拆分国家
    currentParams.b_group_by_market = false;
    // 分平台设备获取数（是否为堆叠柱图按设备拆分）
    // user Engagement 页面 接口默认 b_ampere_group_by_device 特点by device
    currentParams.b_ampere_group_by_device = isByDevice;
    currentParams.b_group_by_device = isByDevice;
    // 分日期获取数据（是否为折线图数据）
    currentParams.b_group_by_date = isByDate;

    // 是否请求环比参数重置
    currentParams.b_growth_ratio = false;

    const res = await fetchPCConsoleKPITrendsCompareTrends(currentParams);
    const data = res?.data?.[0]?.metric_value_list;

    const { start_date: startDate = '', end_date: endDate = '' } = res?.req;

    if (returnTime) {
      return { data, startDate, endDate };
    }

    return data;
  };

  // 获取表格数据 + 柱图饼图相同数据源
  const getTableBarPieData = async ({
    params,
    // 是否需要请求折线图的 others 数据
    shouldFetchLineChartOtherData,
  }: {
    params: Record<string, any>;
    shouldFetchLineChartOtherData?: boolean;
  }) => {
    const currentParams = { ...params };
    // 获取环比
    currentParams.b_growth_ratio = true;

    // 包括 global 的数据（表格需要 global 行，需传入 global + 所有特定国家）
    if (currentParams.markets?.includes('global')) {
      currentParams.markets = ['global', ...allMarkets];
    }

    const res = await fetchPCConsoleKPITrendsCompareTrends(currentParams);

    // 更新请求时间
    const startDate = res?.req?.start_date;
    const endDate = res?.req?.end_date;
    // 时间筛选器，使用响应数据的时间范围
    updateFetchParamsTime(startDate, endDate);

    const data = res?.data?.[0]?.metric_value_list;

    const formattedTableData = data?.map((item: any) => {
      return {
        ...item,
        name: marketsLocalMap[item?.market]?.en,
        name_cn: marketsLocalMap[item?.market]?.cn,
        metrics: [
          {
            name: item.metric,
            value: item.value,
            dod: item.growth_ratio,
          },
        ],
      };
    });

    formattedTableData?.sort((a: any, b: any) => b.value - a.value);

    // 用于计算饼图 other 指标数据
    const topList = getTopList(formattedTableData);
    // 获取首行固定数据
    const marketType = getMaketsType(params.regions, params.regions);
    const firstFixedData = data?.length ? getFixedTableData(marketType, topList) : [];

    setMarketFixedTableData(firstFixedData);

    setTopList(topList);

    const formattedTableDataWithoutGlobal = formattedTableData?.filter(
      (item: { market: string }) => item.market !== 'global',
    );

    const barPieChartData = formattedTableDataWithoutGlobal
      ?.slice(0, COUNTRIES_CHART_TOP_COUNT)
      ?.map((item: any) => {
        return {
          ...item,
          market: item.market,
          name: marketsLocalMap[item?.market]?.en,
          name_cn: marketsLocalMap[item?.market]?.cn,
        };
      });

    /**
     * Other 数据需依赖 Top 20 数据，所以需要在宽表请求后根据 Top 20 获取其他国家查询数据
     * 柱图 + 饼图 Others 数据查询
     * 折线图 Others 数据查询（当前图标类型为折线图，切换筛选器触发请求）
     */
    if (formattedTableDataWithoutGlobal?.length > COUNTRIES_CHART_TOP_COUNT) {
      const topMarkets = barPieChartData?.map((item: any) => item?.market);
      const otherMarkets = currentParams?.markets?.filter((item: string) => {
        return item !== 'global' && item !== OTHERS_MARKET && !topMarkets.includes(item);
      });

      // 请求普通柱图饼图的 others 数据，发起请求并记录请求 promise
      const fetchBarPieOthersData = getOthersData({
        params: { ...currentParams, markets: otherMarkets },
        isByDevice: false,
      });

      // 请求 by device 的 others 数据，发起请求并记录请求 promise
      const fetchByDeviceOthersData = getOthersData({
        params: {
          ...currentParams,
          markets: otherMarkets,
        },
        isByDevice: true,
      });

      // 请求 by date 的折线图 others 数据，发起请求并记录请求 promise
      const fetchLineChartOthersData = shouldFetchLineChartOtherData
        ? getOthersData({
            params: {
              ...currentParams,
              markets: otherMarkets,
            },
            isByDate: true,
            returnTime: true,
          })
        : null;

      // 等待普通柱图饼图 others 数据请求 promise 响应结果
      const otherData = await fetchBarPieOthersData;

      // 将 Others 数据添加到柱图饼图数据中
      barPieChartData.push({
        ...otherData,
        market: 'others',
        metrics: otherData,
        // growth_ratio: 0,
        // name: 'Others',
        // name_cn: '其他',
      });

      // 等待 by device others 数据请求 promise 响应结果
      const byDeviceOtherData = await fetchByDeviceOthersData;

      const byDeviceOthersData = byDeviceOtherData?.map((item: any) => ({
        ...item,
        market: 'others',
        // name_cn: '其他',
        // name: 'Others',
      }));
      const others = formatDeviceBarData(byDeviceOthersData);

      // 更新按平台设备的 Others 数据
      setDeviceBarOthersData(others);

      if (shouldFetchLineChartOtherData) {
        // 等待 by date 的折线图 others 数据请求 promise 响应结果
        const { data, startDate, endDate } = await fetchLineChartOthersData;

        const lineChartOthersData = data?.map((item: any) => ({
          ...item,
          market: 'others',
        }));

        const formattedData = formatLineChartData(lineChartOthersData);

        // 补齐折线空时间数据
        const filledEmptyDateOthersData = formattedData.map((item) => {
          return {
            ...item,
            history: fillInEmptyData({
              currentMetricData: item.history,
              dateType: currentParams.granularity,
              responseQueryDate: { startDate, endDate },
            }),
          };
        });

        setMarketLineOthersData(filledEmptyDateOthersData);
      }
    } else {
      // 没有 others 时重置堆叠柱图\折线图 others 数据状态
      setDeviceBarOthersData([]);
      setMarketLineOthersData([]);
    }

    setLoading(false);
    // 表格数据
    // setMarketTableList(formattedTableData);
    setMarketTableList(topList.list);
    // 普通柱图饼图数据
    setMarketBarPieChartList(barPieChartData);
  };

  // 获取柱状体分平台数据
  const getByPlatformData = async (params: any) => {
    const currentParams = { ...params };
    // 分平台设备获取数
    // user Engagement 页面 接口默认 b_ampere_group_by_device 特点by device
    currentParams.b_ampere_group_by_device = true;
    currentParams.b_group_by_device = false;

    // global 时传入所有特定国家地区
    if (currentParams.markets?.includes('global')) {
      currentParams.markets = allMarkets;
    }

    const res = await fetchPCConsoleKPITrendsCompareTrends(currentParams);
    const data = res?.data?.[0]?.metric_value_list;

    const startDate = res?.req?.start_date;
    const endDate = res?.req?.end_date;
    // 时间筛选器，使用响应数据的时间范围
    updateFetchParamsTime(startDate, endDate);

    // 修改为对应格式，保留前 5 项
    const formattedData = formatDeviceBarData(data);
    setDeviceBarData(formattedData);
  };

  // 获取折线图数据
  const getLineChartData = async (params: any, isChartTypeChange?: boolean) => {
    let currentParams: Record<string, any> = { ...params };

    // global 时传入所有特定国家地区
    if (currentParams.markets?.includes('global')) {
      currentParams.markets = allMarkets;
    }

    let othersMarkets;

    // 通过修改图表类型触发请求
    if (isChartTypeChange) {
      //  需要格式化参数
      const formattedParams = formatFetchParams(currentParams);

      // 如果有前 20 项的值时，地区只传这 20 项
      if (topMarketsRef.current?.length) {
        othersMarkets = formattedParams.markets?.filter((item: string) => {
          return item !== OTHERS_MARKET && !topMarketsRef.current?.includes(item);
        });
        formattedParams.markets = topMarketsRef.current.filter((item: string) => {
          return item !== OTHERS_MARKET;
        });
      }

      currentParams = formattedParams;
    }

    // 折线图数据按照时间拆分
    currentParams.b_group_by_date = true;

    setMarketLineLoading(true);

    try {
      // 请求折线图数据，发起请求并记录请求 promise
      const fetchLineChartData = fetchPCConsoleKPITrendsCompareTrends(currentParams);

      // 请求折线图 others 数据，发起请求并记录请求 promise
      const shouldFetchLineChartOtherData = isChartTypeChange && othersMarkets?.length;
      const fetchLineChartOthersData = shouldFetchLineChartOtherData
        ? getOthersData({
            params: {
              ...currentParams,
              markets: othersMarkets,
            },
            isByDate: true,
            returnTime: true,
          })
        : null;

      // 等待的折线图数据请求 promise 响应结果
      const res = await fetchLineChartData;
      const data = res?.data?.[0]?.metric_value_list;

      const { start_date: startDate = '', end_date: endDate = '' } = res?.req;

      const formattedData = formatLineChartData(data);

      // 补齐折线空时间数据
      const filledEmptyDateData = formattedData.map((item) => {
        return {
          ...item,
          history: fillInEmptyData({
            currentMetricData: item.history,
            dateType: currentParams.granularity,
            responseQueryDate: { startDate, endDate },
          }),
        };
      });

      // 折线图 others 数据处理
      if (shouldFetchLineChartOtherData) {
        // 等待 by date 的折线图 others 数据请求 promise 响应结果
        const { data, startDate, endDate } = await fetchLineChartOthersData;

        const lineChartOthersData = data?.map((item: any) => ({
          ...item,
          market: 'others',
        }));

        const formattedData = formatLineChartData(lineChartOthersData);

        // 补齐折线空时间数据
        const filledEmptyDateOthersData = formattedData.map((item) => {
          return {
            ...item,
            history: fillInEmptyData({
              currentMetricData: item.history,
              dateType: currentParams.granularity,
              responseQueryDate: { startDate, endDate },
            }),
          };
        });

        setMarketLineOthersData(filledEmptyDateOthersData);
      }

      setMarketLineLoading(false);
      setMarketLineData(filledEmptyDateData);
    } catch (error) {
      setMarketError(true);
      setMarketLineLoading(false);
      setMarketLineOthersData([]);
    }
  };

  /**
   * 请求页面数据
   * 表格数据（柱图） + 堆叠柱图数据
   * 当前为折线图时，同时请求折线图数据
   */
  const getPageData = async (params: any, requestSeq: number) => {
    // global 时需传入所有 markets，无 markets 时暂不请求
    if (!allMarkets?.length) return;

    // 格式化表单值为当前接口参数所需格式
    const formattedParams = formatFetchParams(params);

    setMarketLoading(true);

    // 重新请求时将 topMarketsRef 标识置空
    topMarketsRef.current = [];

    try {
      // 筛选器参数变更触发请求时
      const shouldFetchLineChartData = ['line', 'stackedLine'].includes(marketChartType);

      // 请求表格及堆叠柱图数据
      await Promise.all([
        getTableBarPieData({
          params: formattedParams,
          shouldFetchLineChartOtherData: shouldFetchLineChartData,
        }),
        ...(hasByDeviceBar ? [getByPlatformData(formattedParams)] : []),
        ...(shouldFetchLineChartData ? [getLineChartData(formattedParams)] : []),
      ]);

      if (requestSeq !== fetchSequenceRef.current) {
        return;
      }

      setMarketLoading(false);
      setMarketError(false);
    } catch (error) {
      if (requestSeq !== fetchSequenceRef.current) {
        return;
      }
      console.log('fetch data error:', error);
      setMarketError(true);
      setMarketLoading(false);
    }
  };

  // 初始及筛选器变更请求数据
  useEffect(() => {
    // 是否请求完 basic 接口
    if (isBasicLoading || filterLoading) return;

    // global 时需传入所有 markets，无 markets 时暂不请求
    if (!allMarkets?.length) return;

    //  请求数据后自动触发的参数时间变化，不触发请求
    if (!shouldFetchDataByFetchParamsChangeRef.current) return;

    let newFetchParams = { ...fetchParams };

    // 获取继承参数，只初始化执行一次
    if (isOperateDetail && !hasExtendedRef.current) {
      const extendParams: Record<string, any> = getExtendParams?.() || {};

      const isValidDateType = extendParams?.date_type === newFetchParams.date_type;

      newFetchParams = {
        ...newFetchParams,
        ...extendParams,
        // 继承的参数，时间粒度不生效，使用默认时间及粒度
        ...(!isValidDateType
          ? {
              date_type: newFetchParams.date_type,
              start_date: newFetchParams.start_date,
              end_date: newFetchParams.end_date,
            }
          : {}),
      };

      hasExtendedRef.current = true;
    }

    // 月粒度时间，从日粒度默认值切换为月粒度时会遗留具体日期，将日期格式化为最后一天
    if (newFetchParams.date_type === 'month') {
      if (newFetchParams.start_date) {
        newFetchParams.start_date = moment(newFetchParams.start_date).startOf('month').format('YYYY-MM-DD');
      }
      if (newFetchParams.end_date) {
        newFetchParams.end_date = moment(newFetchParams.end_date).endOf('month').format('YYYY-MM-DD');
      }
    }

    const fetchSignature = buildFetchSignature(newFetchParams);
    if (inFlightFetchSignatureRef.current === fetchSignature) {
      return;
    }

    inFlightFetchSignatureRef.current = fetchSignature;
    const requestSeq = ++fetchSequenceRef.current;

    // 请求表格及柱图数据
    getPageData(newFetchParams, requestSeq).finally(() => {
      if (inFlightFetchSignatureRef.current === fetchSignature) {
        inFlightFetchSignatureRef.current = null;
      }
    });

    // 获取最新数据时间
    updateEarliestDateData?.({
      dataSources: newFetchParams.data_sources,
      metrics: newFetchParams.metric,
      regionType: newFetchParams.region_type,
      regions: newFetchParams.regions,
      markets: newFetchParams.markets,
      dateType: newFetchParams.date_type,
    });

    // 避免添加不需要的监听项，导致监听项变动时重复触发请求
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchParams, hasByDeviceBar, allMarkets, filterLoading, isBasicLoading, marketChartType]);

  return {
    marketTableList,
    topDeviceBarData,
    topLineChartData,
    countriesChartDisplayCount: COUNTRIES_CHART_DISPLAY_COUNT,
    marketBarPieChartList,
    topList,
    marketLineLoading,
    countryLineLoading,
    setCountryLineLoading,
    marketLoading,
    marketError,
    getLineChartData,
    marketFixedTableData,
  };
}

export default useGetCountriesEngagementData;
